import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';
import LoginModal from '../components/auth/LoginModal';
import {
  hasFullAccess,
  roleMatches,
  getRoleDisplayName,
} from '../config/hospitalRoles';

const AuthContext = createContext(null);

// Configure axios defaults
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
console.log('🔍 Debug - API_URL from env:', API_URL);
console.log('🔍 Debug - All REACT_APP env vars:', Object.keys(process.env).filter(key => key.startsWith('REACT_APP_')));
axios.defaults.baseURL = API_URL;

// Add request interceptor to include token
axios.interceptors.request.use(
  (config) => {
    console.log('🚀 Debug - Making request to:', config.url);
    console.log('🚀 Debug - Full URL will be:', config.baseURL + config.url);
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Global variables to manage auth state outside component
let authContextRef = null;
let pendingRequestsQueue = [];

// Add response interceptor to handle token refresh
axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const refreshToken = localStorage.getItem('refresh_token');
        if (refreshToken) {
          const response = await axios.post('/v1/auth/refresh', {
            refresh_token: refreshToken
          });

          const { access_token } = response.data;
          localStorage.setItem('access_token', access_token);
          
          // Retry original request with new token
          originalRequest.headers.Authorization = `Bearer ${access_token}`;
          return axios(originalRequest);
        }
      } catch (refreshError) {
        // Refresh failed - show modal and queue request
        localStorage.clear();
        
        if (authContextRef) {
          // Queue this request to retry after re-login
          return new Promise((resolve, reject) => {
            pendingRequestsQueue.push({ resolve, reject, config: originalRequest });
            authContextRef.showLoginModal();
          });
        }
      }
    }

    return Promise.reject(error);
  }
);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showLoginModal, setShowLoginModal] = useState(false);

  /**
   * Helper function to extract error messages from API responses
   * Handles both FastAPI validation errors (arrays) and string errors
   */
  const extractErrorMessage = (err, defaultMessage = 'An error occurred') => {
    if (!err.response?.data?.detail) {
      return defaultMessage;
    }

    const detail = err.response.data.detail;

    // If detail is an array (FastAPI validation errors), extract the first message
    if (Array.isArray(detail) && detail.length > 0) {
      return detail[0].msg || detail[0].message || defaultMessage;
    }

    // If detail is a string, use it directly
    if (typeof detail === 'string') {
      return detail;
    }

    return defaultMessage;
  };

  // Check if user is logged in on mount
  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const token = localStorage.getItem('access_token');
      if (token) {
        const response = await axios.get('/v1/auth/me');
        setUser(response.data);
      }
    } catch (err) {
      console.error('Auth check failed:', err);
      localStorage.clear();
    } finally {
      setLoading(false);
    }
  };

  const refreshUser = async () => {
    try {
      const token = localStorage.getItem('access_token');
      if (token) {
        const response = await axios.get('/v1/auth/me');
        setUser(response.data);
        localStorage.setItem('user', JSON.stringify(response.data));
      }
    } catch (err) {
      console.error('User refresh failed:', err);
    }
  };

  const login = async (username, password) => {
    setError(null);
    try {
      const response = await axios.post('/v1/auth/login', {
        username,
        password
      });

      const { user, access_token, refresh_token, force_password_change } = response.data;

      // Store tokens
      localStorage.setItem('access_token', access_token);
      localStorage.setItem('refresh_token', refresh_token);
      localStorage.setItem('user', JSON.stringify(user));

      setUser(user);

      // Process any queued requests after successful login
      if (pendingRequestsQueue.length > 0) {
        const queuedRequests = [...pendingRequestsQueue];
        pendingRequestsQueue.length = 0; // Clear the queue

        // Retry all queued requests
        queuedRequests.forEach(async ({ resolve, reject, config }) => {
          try {
            config.headers.Authorization = `Bearer ${access_token}`;
            const response = await axios(config);
            resolve(response);
          } catch (error) {
            reject(error);
          }
        });

        // Close modal after processing requests
        setShowLoginModal(false);
      }

      // Check if password change is required
      if (force_password_change) {
        return { success: true, forcePasswordChange: true };
      }

      return { success: true };
    } catch (err) {
      const errorMessage = extractErrorMessage(err, 'Login failed. Please try again.');
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  const logout = async () => {
    try {
      await axios.post('/v1/auth/logout');
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      // Clear local storage and state
      localStorage.clear();
      setUser(null);
      // Don't auto-redirect, let the calling component handle it
    }
  };

  const changePassword = async (currentPassword, newPassword, confirmPassword) => {
    setError(null);
    try {
      await axios.post('/v1/auth/change-password', {
        current_password: currentPassword,
        new_password: newPassword,
        confirm_password: confirmPassword
      });
      return { success: true };
    } catch (err) {
      const errorMessage = extractErrorMessage(err, 'Password change failed.');
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  const forgotPassword = async (email) => {
    setError(null);
    try {
      const response = await axios.post('/v1/auth/forgot-password', {
        email
      });
      return { success: true, message: response.data.message };
    } catch (err) {
      const errorMessage = extractErrorMessage(err, 'Request failed.');
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  const resetPassword = async (token, newPassword, confirmPassword) => {
    setError(null);
    try {
      await axios.post('/v1/auth/reset-password', {
        token,
        new_password: newPassword,
        confirm_password: confirmPassword
      });
      return { success: true };
    } catch (err) {
      const errorMessage = extractErrorMessage(err, 'Password reset failed.');
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  const hasPermission = (permission) => {
    if (!user) return false;
    if (hasFullAccess(user)) return true;
    
    // Check exact permission
    if (user.permissions?.includes(permission)) return true;
    
    // Check wildcard permission
    const [module] = permission.split(':');
    if (user.permissions?.includes(`${module}:*`)) return true;
    
    return false;
  };

  const hasRole = (roles) => {
    if (!user) return false;
    return roleMatches(user.role_name, roles);
  };

  const roleDisplayName = user
    ? (user.role_display_name || getRoleDisplayName(user.role_name))
    : '';

  // Set up reference for axios interceptor
  useEffect(() => {
    authContextRef = {
      showLoginModal: () => setShowLoginModal(true)
    };
    return () => {
      authContextRef = null;
    };
  }, []);

  const value = {
    user,
    loading,
    error,
    showLoginModal,
    setShowLoginModal,
    login,
    logout,
    changePassword,
    forgotPassword,
    resetPassword,
    checkAuth,
    refreshUser,
    hasPermission,
    hasRole,
    getRoleDisplayName: () => roleDisplayName,
    roleDisplayName,
    isAuthenticated: !!user
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
      <LoginModal />
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
